/* eslint-disable react/prop-types */
import { Box, Card, Typography, Divider } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { riskLevelData, riskLevelColor } from "@/lib/listData";

function RiskLevelsCard({ hazard, getRiskLevelColor }) {
  const { t } = useTranslation();
  const theme = useTheme();

  const riskGroups = [
    {
      key: "people",
      label: t("hazards.people"),
      severity: hazard.severity_people,
    },
    {
      key: "environment",
      label: t("hazards.environment"),
      severity: hazard.severity_environment,
    },
    {
      key: "asset",
      label: t("hazards.asset"),
      severity: hazard.severity_asset,
    },
    {
      key: "reputation",
      label: t("hazards.reputation"),
      severity: hazard.severity_reputation,
    },
  ];

  // Helper function to get unique risk levels for legend
  const getUniqueRiskLevels = () => {
    const uniqueLevels = new Map();

    // Iterate through all likelihood and severity combinations
    for (let likelihood = 1; likelihood <= 5; likelihood++) {
      for (let severity = 1; severity <= 5; severity++) {
        const levelName = riskLevelData[likelihood]?.[severity];
        const levelColor = riskLevelColor[likelihood]?.[severity];

        if (levelName && levelColor && !uniqueLevels.has(levelName)) {
          uniqueLevels.set(levelName, levelColor);
        }
      }
    }

    return Array.from(uniqueLevels.entries()).map(([name, color]) => ({
      name,
      backgroundColor: color.backgroundColor,
      textColor: color.color,
    }));
  };

  const riskLevels = getUniqueRiskLevels();

  return (
    <Card
      elevation={2}
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          p: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: alpha(theme.palette.warning.main, 0.05),
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
          {t("hazards.riskLevels")}
        </Typography>
      </Box>
      <Box
        sx={{
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        {riskGroups.map((group) => (
          <Box
            key={group.key}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 1.5,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: alpha(theme.palette.background.paper, 0.5),
            }}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                bgcolor: getRiskLevelColor(hazard.likelihood, group.severity)
                  ?.backgroundColor,
                border: `2px solid ${
                  getRiskLevelColor(hazard.likelihood, group.severity)
                    ?.backgroundColor === "white"
                    ? "#ccc"
                    : "transparent"
                }`,
                flexShrink: 0,
              }}
            />
            <Typography variant="body2" fontWeight="500">
              {group.label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Risk Level Legend */}
      <Divider />
      <Box
        sx={{
          p: 2,
          bgcolor: alpha(theme.palette.background.paper, 0.5),
        }}
      >
        <Typography
          variant="caption"
          fontWeight="bold"
          color="text.primary"
          sx={{ mb: 1.5, display: "block" }}
        >
          {t("hazards.riskLevelLegend")}
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr",
              md: "1fr 1fr",
            },
            gap: 1,
          }}
        >
          {riskLevels.map((riskLevel, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: riskLevel.backgroundColor,
                  border: `1px solid ${riskLevel.backgroundColor === "white" ? "#ccc" : "transparent"}`,
                  flexShrink: 0,
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {riskLevel.name}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Card>
  );
}

export default RiskLevelsCard;
